// Browser-side calls to the billing routes. Every refusal comes back as a
// BillingRequestError carrying the route's human message and machine code.
import type { BillingInterval, PaidPlan } from "@/lib/billing/planConfig";
import type {
  BillingErrorCode,
  BillingState,
  CancelReason,
  PlanChangeKind,
  PlanChangePreview,
} from "@/lib/billing/types";

export class BillingRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: BillingErrorCode | null,
  ) {
    super(message);
    this.name = "BillingRequestError";
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { cache: "no-store", ...init });
  } catch {
    throw new BillingRequestError("Network error — check your connection and try again.", 0, null);
  }
  const body = (await res.json().catch(() => null)) as
    | (T & { error?: string; code?: BillingErrorCode })
    | null;
  if (!res.ok || !body) {
    throw new BillingRequestError(
      body?.error ?? "Something went wrong. Please try again.",
      res.status,
      body?.code ?? null,
    );
  }
  return body;
}

function post<T>(url: string, payload?: unknown): Promise<T> {
  return request<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
}

export function fetchBillingState(): Promise<BillingState> {
  return request("/api/billing/subscription");
}

export function previewPlanChange(plan: PaidPlan, interval: BillingInterval): Promise<PlanChangePreview> {
  return request(`/api/billing/change-plan?plan=${plan}&interval=${interval}`);
}

/** `expected` = what the confirm button showed; the server refuses if it moved. */
export function applyPlanChange(
  plan: PaidPlan,
  interval: BillingInterval,
  expected?: { kind: PlanChangeKind; chargeTodayCents: number },
): Promise<{ kind: PlanChangeKind; state: BillingState }> {
  return post("/api/billing/change-plan", {
    plan,
    interval,
    ...(expected && { expectedKind: expected.kind, expectedChargeCents: expected.chargeTodayCents }),
  });
}

export function cancelSubscription(reason: CancelReason | null): Promise<{ state: BillingState }> {
  return post("/api/billing/cancel", { reason });
}

export function resumeSubscription(): Promise<{ state: BillingState }> {
  return post("/api/billing/resume");
}

/** Creates a Creem checkout and returns its URL (the caller navigates). */
export async function createCheckout(plan: PaidPlan, interval: BillingInterval): Promise<string> {
  const data = await post<{ checkout_url?: string }>("/api/checkout", { plan, interval });
  if (!data.checkout_url) throw new BillingRequestError("Failed to start checkout", 502, null);
  return data.checkout_url;
}

/** Activate the plan from a just-completed checkout (Creem's `checkout_id`). */
export function confirmCheckout(checkoutId: string): Promise<{ activated: boolean; state: BillingState }> {
  return post("/api/billing/checkout-complete", { checkout_id: checkoutId });
}

/** One-time Creem portal link (payment method, invoices). */
export async function createPortalLink(): Promise<string> {
  const data = await post<{ portal_url?: string }>("/api/billing-portal");
  if (!data.portal_url) throw new BillingRequestError("Failed to open the billing portal", 502, null);
  return data.portal_url;
}
